import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { PageHero, Overline } from "@/components/Bits";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { ParallaxImage } from "@/components/ParallaxImage";
import { SecuritySection } from "@/components/SecuritySection";
import { GOALS, IMAGES } from "@/data/content";

export default function Impact() {
  return (
    <>
      <PageHero
        label="Our Impact"
        title="Measured in lives"
        accent="changed."
        subtitle="Numbers tell part of the story. What matters most is what changed because we showed up—an opportunity that wasn't there before, access to something that felt out of reach, or the support someone needed to take the next step. That's how we measure our work and the difference we're here to make."
      />

      {/* Goals */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-28" data-testid="impact-goals">
        <Reveal><Overline>Our First-Year Goals</Overline></Reveal>
        <Stagger className="mt-12 grid grid-cols-2 gap-y-12 lg:grid-cols-4">
          {GOALS.map((g) => (
            <StaggerItem key={g.label}>
              <div className="border-l-2 border-gold pl-5">
                <div className="font-heading text-4xl font-bold tracking-tight md:text-6xl">
                  <CountUp to={g.value} prefix={g.prefix || ""} suffix={g.suffix || ""} />
                </div>
                <p className="mt-3 max-w-[14rem] text-sm leading-relaxed text-muted-foreground">
                  {g.label}
                </p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.1}>
          <p className="mt-10 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            These figures represent our commitments and targets for our inaugural year — a
            transparent promise to the communities we serve, not past results.
          </p>
        </Reveal>
      </section>

      {/* Quote */}
      <section className="relative overflow-hidden border-y border-white/10 brand-ink">
        <ParallaxImage src={IMAGES.stripReading} alt="A child reading" className="absolute inset-0 h-full w-full opacity-40" strength={6} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(13,29,71,0.86)" }} />
        <div className="relative mx-auto max-w-4xl px-5 py-24 text-center md:px-8 md:py-28">
          <Quote className="mx-auto h-10 w-10 text-gold" />
          <Reveal delay={0.06}>
            <p className="mt-8 font-heading text-2xl font-medium leading-snug text-white sm:text-3xl md:text-4xl">
              "When you educate, empower, and elevate a single person, you set in motion a change
              that reaches an entire community."
            </p>
          </Reveal>
          <p className="mt-8 overline text-gold">Thefrent Foundation</p>
        </div>
      </section>

      {/* Where support goes */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-28">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <div>
            <Reveal><Overline>Where Your Support Goes</Overline></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
                Transparent by design.
              </h2>
            </Reveal>
            <Stagger className="mt-8 space-y-5">
              {[
                ["Programs & Scholarships", "78%"],
                ["Community Partnerships", "14%"],
                ["Operations & Administration", "8%"],
              ].map(([label, pct]) => (
                <StaggerItem key={label}>
                  <div>
                    <div className="flex items-center justify-between text-sm font-medium">
                      <span>{label}</span>
                      <span className="text-gold">{pct}</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-gold" style={{ width: pct }} />
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-2xl border border-border">
              <div className="absolute inset-0 z-10 ring-1 ring-inset ring-gold/25" />
              <ParallaxImage src={IMAGES.hands} alt="Community volunteers" className="aspect-[4/3] w-full" />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-14 flex justify-center">
            <Link to="/donate" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy-deep transition-transform hover:-translate-y-0.5" data-testid="impact-donate">
              Become a Supporter <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Founder's Note */}
      <section className="brand-ink text-white" data-testid="founder-note">
        <div className="mx-auto max-w-4xl px-5 py-24 md:px-8 md:py-28">
          <Reveal>
            <p className="overline txt-gold">A Note From Our Founder</p>
          </Reveal>
          <Reveal delay={0.08}>
            <blockquote className="mt-8 font-heading text-2xl font-medium leading-snug tracking-tight text-white sm:text-3xl">
              "I started Thefrent Foundation because I believe opportunity should never depend on
              where you're born or what you can afford. Every scholarship, every program, and every
              partnership is a promise—that someone's potential will be met with real support.
              Thank you for standing with us as we build brighter futures, together."
            </blockquote>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-10 flex items-center gap-4">
              <span className="grid h-14 w-14 place-items-center rounded-full brand-gold-bg font-heading text-lg font-bold text-[#0d1d47]">
                SM
              </span>
              <div>
                <p className="font-script text-3xl leading-none txt-gold">Shawna-Kay McPherson</p>
                <p className="mt-1 text-sm text-white/70">Founder &amp; Director</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <SecuritySection compact />
    </>
  );
}
